/**
 * Inserts structured paragraph runs for the Agent drafting tools (Office.js).
 *
 * Each paragraph can carry inline structure metadata (list, alignment, bold) so the agent does
 * NOT have to make a separate apply_word_structure call for new content. Structure is applied
 * within the same Word.run() immediately after insertion.
 *
 * Paragraph indices in the agent API match DocumentReader.getFullDocumentContext() (non-empty only).
 */

/* global Word */

import { log, logError } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';
import { applyStandardOutlineNumbering, applyBulletAllLevels } from './wordListOutline';
import { prependRlmToHebrewParagraphs, textLooksHebrew, trySetSectionRtl } from './wordDocumentRtl';
import { TrackChangesParser } from '../utils/TrackChangesParser';
import { OoxmlPackageWrapper } from '../utils/OoxmlPackageWrapper';
import { DocumentReader } from './DocumentReader';
import {
  resolveInsertionStyleReference,
  type InsertionStyleReference,
} from './insertionParagraphStyle';

async function syncWithLabel(context: Word.RequestContext, label: string): Promise<void> {
  try {
    await context.sync();
    log('[AgentDocumentInserter] sync OK', label);
  } catch (e: unknown) {
    logError('[AgentDocumentInserter] sync FAILED', label, formatOfficeJsError(e));
    throw e;
  }
}

export type InsertParagraphsMode = 'fill_empty' | 'after_agent_paragraph';

/**
 * Per-paragraph structure spec. Strings are also accepted and treated as plain paragraphs.
 *
 * list: 'numbered' | 'bullet'
 *   Consecutive paragraphs sharing the same list type form one Word list.
 *   A non-list paragraph or a change in type starts a new list.
 *   ANY leading numeric/bullet prefix in `text` is auto-stripped ("1. ", "2) ", "• " …).
 *
 * level: 0-based outline level (0 = 1., 2., … — main sections; 1 = 1.1, 1.2 — sub-clauses;
 *   2 = 1.1.1, …). Required for multilevel **numbered** lists; do not use `bullet` for numbered sub-points.
 * numbering: Word `ListNumbering` (`Arabic` = standard 1,2,3 / multilevel — Word's label for Western decimals).
 * align: paragraph alignment applied after insert.
 * bold: bold the entire paragraph text.
 */
/** Agent may send `list` as a string or as `{ level, numbering }` (same reference every time would group; distinct objects did not). */
export type ParagraphSpecList =
  | 'numbered'
  | 'bullet'
  | {
      level?: number;
      numbering?: 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman';
      type?: 'numbered' | 'bullet';
      bullet?: boolean;
    };

export type ParagraphSpec = {
  text: string;
  list?: ParagraphSpecList;
  level?: number;
  numbering?: 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman';
  align?: 'Left' | 'Centered' | 'Right' | 'Justified';
  bold?: boolean;
};

/** After normalizeSpec, `list` is always a string or omitted (never a rich object). */
type NormalizedSpec = Required<Pick<ParagraphSpec, 'text' | 'level' | 'numbering'>> & {
  list?: 'numbered' | 'bullet';
  align?: ParagraphSpec['align'];
  bold?: ParagraphSpec['bold'];
};

/** Strip leading "1. " / "2) " / "(3) " / "א. " / "• " etc. from text before inserting into a list */
function stripListPrefix(text: string): string {
  return text
    .replace(/^\s*\(?\d+[\.\)]\s*/, '') // "1. " | "1) " | "(1) "
    .replace(/^\s*\d+\s+/, '')           // "1 " (space after digit)
    .replace(/^\s*[א-ת][\.\)]\s*/, '')  // "א. " Hebrew letter prefix
    .replace(/^\s*[•\-\*]\s*/, '')       // "• " "- " "* "
    .trim();
}

/** Party / routing lines must not get Word outline numbers (common model mistake → "everything numbered"). */
function shouldOmitListNumberingForBoilerplate(text: string, list: 'numbered' | 'bullet' | undefined, level: number): boolean {
  if (list !== 'numbered' || level !== 0) return false;
  const t = (text || '').trim();
  if (!t) return false;
  if (/^\s*(בין|לבין)\s*:/.test(t)) return true;
  if (/^\s*(Between|And)\s*:/i.test(t)) return true;
  return false;
}

function normalizeSpec(raw: string | ParagraphSpec): NormalizedSpec {
  const spec: ParagraphSpec = typeof raw === 'string' ? { text: raw } : { ...raw };
  const rawList = spec.list as ParagraphSpecList | undefined;

  let list: 'numbered' | 'bullet' | undefined;
  let level = Number(spec.level ?? 0) || 0;
  let numbering: ParagraphSpec['numbering'] = spec.numbering ?? 'Arabic';

  if (rawList === 'numbered' || rawList === 'bullet') {
    list = rawList;
  } else if (rawList && typeof rawList === 'object') {
    const o = rawList;
    if (o.type === 'bullet' || o.bullet === true) {
      list = 'bullet';
    } else {
      list = 'numbered';
      if (o.numbering != null) numbering = o.numbering;
    }
    if (o.level !== undefined) level = Number(o.level) || 0;
  }

  level = Math.max(0, Math.min(8, level));
  if (shouldOmitListNumberingForBoilerplate(spec.text ?? '', list, level)) {
    list = undefined;
  }
  const text = list ? stripListPrefix(spec.text ?? '') : (spec.text ?? '');

  return {
    ...spec,
    text,
    list,
    level,
    numbering: numbering ?? 'Arabic',
  };
}

/**
 * @param paragraphs - Strings or ParagraphSpec objects. Consecutive specs with the same `list` type
 *   form one Word list automatically. Structure is applied within the same Word.run().
 * @param mode - fill_empty | after_agent_paragraph
 * @param afterAgentParagraphIndex - Required when mode is after_agent_paragraph.
 */
export async function insertParagraphsForAgent(
  paragraphs: (string | ParagraphSpec)[],
  mode: InsertParagraphsMode,
  afterAgentParagraphIndex?: number
): Promise<{ ok: boolean; inserted: number; detail?: string }> {
  const allSpecs: NormalizedSpec[] = (Array.isArray(paragraphs) ? paragraphs : []).map(normalizeSpec);
  if (allSpecs.length === 0 || allSpecs.every((s) => !s.text.trim())) {
    return { ok: false, inserted: 0, detail: 'No non-empty paragraphs to insert' };
  }

  try {
    const specSummary = allSpecs.map((s, i) => ({
      i,
      len: s.text.length,
      list: s.list ?? null,
      level: s.level,
      align: s.align ?? null,
      bold: s.bold ?? null,
    }));
    log('[AgentDocumentInserter] start', {
      mode,
      paragraphSpecs: allSpecs.length,
      afterAgentParagraphIndex: afterAgentParagraphIndex ?? null,
      specSummarySample: specSummary.slice(0, 8),
      specSummaryTotal: specSummary.length,
    });

    await Word.run(async (context) => {
      const rtlSample = allSpecs.map((s) => s.text).join('\n');
      let sectionRtlOk = false;
      if (textLooksHebrew(rtlSample)) {
        sectionRtlOk = await trySetSectionRtl(context);
      }

      const body = context.document.body;
      const pc = body.paragraphs;
      pc.load('items');
      await syncWithLabel(context, 'load body.paragraphs.items');

      const items = pc.items as unknown as any[];
      for (const item of items) (item as any).load('text');
      await syncWithLabel(context, 'load paragraph text');

      const wordText = (i: number) => String((items[i] as any).text ?? '').trim();

      // --- Phase 1: insert all paragraphs, collect Word paragraph anchors per spec ---
      // insertedAnchors[i] corresponds to allSpecs[i]
      const insertedAnchors: any[] = new Array(allSpecs.length).fill(null);

      if (mode === 'fill_empty') {
        const hasContent = items.some((_, i) => wordText(i).length > 0);
        if (hasContent) {
          throw new Error(
            'fill_empty requires a document with no non-empty paragraphs; use after_agent_paragraph.'
          );
        }

        // Insert first spec into the existing blank paragraph (or a new one)
        let anchor: any;
        if (items.length === 0) {
          anchor = (body as any).insertParagraph(allSpecs[0].text, Word.InsertLocation.start);
        } else {
          items[0].getRange().insertText(allSpecs[0].text, Word.InsertLocation.replace);
          anchor = items[0];
        }
        insertedAnchors[0] = anchor;

        // Chain the rest
        for (let i = 1; i < allSpecs.length; i++) {
          anchor = anchor.insertParagraph(allSpecs[i].text, Word.InsertLocation.after);
          insertedAnchors[i] = anchor;
        }
        log('[AgentDocumentInserter] phase1 fill_empty chain done', { insertedCount: allSpecs.length });
      } else {
        // after_agent_paragraph
        const idx = afterAgentParagraphIndex;
        if (idx === undefined || idx === null || Number.isNaN(Number(idx))) {
          throw new Error('after_agent_paragraph_index is required for after_agent_paragraph mode');
        }
        const targetAgent = Math.max(0, Math.floor(Number(idx)));
        let agentSeen = -1;
        let targetWordIdx = -1;
        for (let wi = 0; wi < items.length; wi++) {
          if (wordText(wi).length === 0) continue;
          agentSeen++;
          if (agentSeen === targetAgent) { targetWordIdx = wi; break; }
        }
        if (targetWordIdx < 0) {
          throw new Error(
            `after_agent_paragraph_index ${targetAgent} not found (not enough non-empty paragraphs).`
          );
        }

        // Insert specs chained after the target doc paragraph
        let anchor: any = items[targetWordIdx];
        for (let i = 0; i < allSpecs.length; i++) {
          anchor = anchor.insertParagraph(allSpecs[i].text, Word.InsertLocation.after);
          insertedAnchors[i] = anchor;
        }
        log('[AgentDocumentInserter] phase1 after_agent_paragraph chain done', {
          insertedCount: allSpecs.length,
          afterIdx: afterAgentParagraphIndex,
        });
      }

      await syncWithLabel(context, 'phase1 insert paragraphs');

      // --- Phase 2: apply alignment and bold directly to anchors ---
      for (let i = 0; i < allSpecs.length; i++) {
        const spec = allSpecs[i];
        const para = insertedAnchors[i];
        if (!para) continue;
        if (spec.align) para.alignment = spec.align;
        if (spec.bold) para.font.bold = true;
      }
      await syncWithLabel(context, 'phase2 align/bold');

      // --- Phase 3: apply Word-native lists ---
      // Group consecutive same-type list items into list groups (a gap breaks the group)
      type ListGroup = { type: 'numbered' | 'bullet'; indices: number[] };
      const groups: ListGroup[] = [];
      let currentGroup: ListGroup | null = null;

      for (let i = 0; i < allSpecs.length; i++) {
        const spec = allSpecs[i];
        if (!spec.list) {
          currentGroup = null;
          continue;
        }
        if (!currentGroup || currentGroup.type !== spec.list) {
          currentGroup = { type: spec.list, indices: [] };
          groups.push(currentGroup);
        }
        currentGroup.indices.push(i);
      }

      log('[AgentDocumentInserter] phase3 list groups', {
        groupCount: groups.length,
        groups: groups.map((g) => ({ type: g.type, specIndices: g.indices, levels: g.indices.map((ix) => allSpecs[ix].level ?? 0) })),
      });

      let groupIdx = 0;
      for (const group of groups) {
        if (group.indices.length === 0) continue;
        groupIdx += 1;
        log('[AgentDocumentInserter] phase3 group start', {
          groupIdx,
          type: group.type,
          specIndices: group.indices,
          paragraphCount: group.indices.length,
        });

        // Only detach paragraphs that are already list items — detachFromList() throws GeneralException otherwise.
        for (const i of group.indices) {
          const p = insertedAnchors[i];
          if (p) (p as Word.Paragraph).load('isListItem');
        }
        await syncWithLabel(context, `phase3 g${groupIdx} load isListItem before detach`);
        for (const i of group.indices) {
          const p = insertedAnchors[i] as Word.Paragraph | undefined;
          if (p?.isListItem) {
            p.detachFromList();
          }
        }
        await syncWithLabel(context, `phase3 g${groupIdx} detachFromList (where needed)`);

        const firstPara = insertedAnchors[group.indices[0]];
        const list = firstPara.startNewList();
        await syncWithLabel(context, `phase3 g${groupIdx} startNewList`);

        if (group.type === 'numbered') {
          const num = allSpecs[group.indices[0]].numbering;
          log('[AgentDocumentInserter] phase3 apply outline numbering', { groupIdx, numbering: num });
          applyStandardOutlineNumbering(list, num);
        } else {
          applyBulletAllLevels(list, 'Solid');
        }
        await syncWithLabel(context, `phase3 g${groupIdx} setLevelNumbering/setLevelBullet (0–8)`);

        list.load('id');
        await syncWithLabel(context, `phase3 g${groupIdx} list.load id`);
        const listId = list.id;
        log('[AgentDocumentInserter] phase3 list id', { groupIdx, listId });

        // startNewList() already attaches the first paragraph — do NOT attachToList again (GeneralException).
        for (let k = 1; k < group.indices.length; k++) {
          const specIdx = group.indices[k];
          const level = allSpecs[specIdx].level ?? 0;
          insertedAnchors[specIdx].attachToList(listId, level);
        }
        await syncWithLabel(context, `phase3 g${groupIdx} attachToList (rest only, count=${Math.max(0, group.indices.length - 1)})`);
      }

      if (textLooksHebrew(rtlSample)) {
        if (!sectionRtlOk) {
          sectionRtlOk = await trySetSectionRtl(context);
        }
        if (!sectionRtlOk) {
          const n = await prependRlmToHebrewParagraphs(context);
          log('[AgentDocumentInserter] RTL RLM fallback after lists', { paragraphs: n });
        }
      }
    });

    const inserted = allSpecs.filter((s) => s.text.trim().length > 0).length;
    log('[AgentDocumentInserter] inserted', { count: inserted, mode });
    return { ok: true, inserted };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[AgentDocumentInserter] failed', {
      mode,
      msg,
      office: formatOfficeJsError(e),
    });
    return { ok: false, inserted: 0, detail: msg };
  }
}

export type MultiAnchorInsertion = {
  text: string;
  afterAgentParagraphIndex: number;
  /** When set, insert via OOXML w:ins with this author (not Word's logged-in user). */
  trackChangesAuthor?: string;
};

function buildAgentIndexToWordIdx(
  items: any[],
  wordText: (i: number) => string,
): Map<number, number> {
  const map = new Map<number, number>();
  let agentSeen = -1;
  for (let wi = 0; wi < items.length; wi++) {
    if (wordText(wi).length === 0) continue;
    agentSeen++;
    map.set(agentSeen, wi);
  }
  return map;
}

async function readChangeTrackingMode(context: Word.RequestContext): Promise<string> {
  const doc = context.document as Word.Document & { changeTrackingMode?: string };
  doc.load('changeTrackingMode');
  await context.sync();
  return String((doc as any).changeTrackingMode ?? '');
}

function isChangeTrackingOn(mode: string): boolean {
  return mode === 'TrackAll' || mode === 'TrackMineOnly';
}

async function setChangeTrackingMode(context: Word.RequestContext, mode: string): Promise<void> {
  (context.document as any).changeTrackingMode = mode;
  await context.sync();
}

async function loadParagraphStylesForIndices(
  context: Word.RequestContext,
  items: any[],
  wordIndices: number[],
): Promise<Map<number, string>> {
  const styles = new Map<number, string>();
  const unique = [...new Set(wordIndices.filter((wi) => wi >= 0 && wi < items.length))];
  for (const wi of unique) {
    items[wi].load('style');
  }
  if (unique.length) {
    await context.sync();
  }
  for (const wi of unique) {
    styles.set(wi, String(items[wi]?.style ?? '').trim());
  }
  return styles;
}

async function applyInsertionParagraphStyle(
  context: Word.RequestContext,
  newPara: Word.Paragraph,
  styleRef: InsertionStyleReference,
  items: any[],
): Promise<void> {
  if (styleRef.refWordIdx != null && items[styleRef.refWordIdx]) {
    const refPara = items[styleRef.refWordIdx] as Word.Paragraph;
    refPara.load('style');
    await context.sync();
    const refStyle = String(refPara.style ?? '').trim();
    if (refStyle) {
      newPara.style = refStyle;
      await context.sync();
      return;
    }
  }
  const fallback = (styleRef.fallbackStyle || '').trim();
  if (!fallback) return;
  try {
    newPara.style = fallback;
    await context.sync();
  } catch (e: unknown) {
    log('[AgentDocumentInserter] could not apply fallback paragraph style', {
      fallback,
      office: formatOfficeJsError(e),
    });
  }
}

/**
 * Same intent as applyInsertionParagraphStyle but queues the property set without
 * its own sync — the caller already has stylesByWordIdx fully loaded (one batched
 * read for the whole insertion set), so there is nothing left to fetch here. Used
 * by the "one go" batched insertion path; skips the speculative fallback-style
 * guess (e.g. "heading N+1") since an invalid style name can fail the shared
 * batch sync for everything queued alongside it — only copies a style read from
 * an existing, therefore guaranteed-valid, paragraph.
 */
function applyInsertionParagraphStyleFast(
  newPara: Word.Paragraph,
  styleRef: InsertionStyleReference,
  stylesByWordIdx: Map<number, string>,
): void {
  if (styleRef.refWordIdx == null) return;
  const refStyle = (stylesByWordIdx.get(styleRef.refWordIdx) ?? '').trim();
  if (refStyle) {
    newPara.style = refStyle;
  }
}

/**
 * Insert a new paragraph after `anchor` with explicit w:ins author.
 *
 * Primary path: insertText with track changes ON (Word builds valid w:ins markup), read
 * getOoxml(), rewrite w:author in the live package, round-trip via insertOoxml replace.
 *
 * Fallback: handcrafted w:ins OOXML merged into the live getOoxml() package body.
 */
async function insertTrackedParagraphAfterAnchor(
  context: Word.RequestContext,
  anchor: Word.Paragraph,
  text: string,
  tcAuthor: string,
  styleSourceParagraph?: Word.Paragraph,
): Promise<Word.Paragraph> {
  const pPrSource = styleSourceParagraph ?? anchor;
  let sourceParagraphOoxml: string | null = null;
  try {
    const anchorOoxml = pPrSource.getOoxml();
    await context.sync();
    sourceParagraphOoxml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(anchorOoxml);
  } catch (e: unknown) {
    log('[AgentDocumentInserter] could not read anchor OOXML for pPr', formatOfficeJsError(e));
  }

  const newPara = anchor.insertParagraph('', Word.InsertLocation.after);
  await context.sync();

  const doc = context.document as Word.Document & { changeTrackingMode?: string };
  doc.load('changeTrackingMode');
  await context.sync();
  const previousMode = String((doc as any).changeTrackingMode ?? '');
  const trackingWasOn = previousMode === 'TrackAll' || previousMode === 'TrackMineOnly';

  const setTracking = async (on: boolean): Promise<void> => {
    (doc as any).changeTrackingMode = on ? 'TrackAll' : 'Off';
    await context.sync();
  };

  const replaceParagraphViaLivePackage = async (
    rawPackage: string,
    paragraphXml: string,
    label: string,
  ): Promise<boolean> => {
    if (!rawPackage?.trim() || !paragraphXml?.trim()) return false;

    const pruned =
      OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(rawPackage, text.trim()) || rawPackage;
    const packageXml = OoxmlPackageWrapper.replaceParagraphInPackage(pruned, paragraphXml);
    if (!packageXml?.trim()) return false;

    const wasOn = trackingWasOn;
    if (wasOn) await setTracking(false);

    const range = newPara.getRange('Whole');
    const rangeWithOoxml = range as Word.Range & {
      insertOoxml?: (xml: string, loc: Word.InsertLocation) => void;
    };
    if (typeof rangeWithOoxml.insertOoxml !== 'function') {
      if (wasOn) await setTracking(true);
      throw new Error('insertOoxml not supported');
    }

    try {
      rangeWithOoxml.insertOoxml(packageXml, Word.InsertLocation.replace);
      await context.sync();
      log('[AgentDocumentInserter] tracked insert OK', { label, packageLen: packageXml.length });
      return true;
    } catch (e: unknown) {
      log('[AgentDocumentInserter] tracked insertOoxml failed', {
        label,
        office: formatOfficeJsError(e),
      });
      return false;
    } finally {
      if (wasOn) await setTracking(true);
    }
  };

  try {
    // Path A: Word creates w:ins via insertText; we override w:author in live OOXML.
    if (!trackingWasOn) await setTracking(true);
    try {
      newPara.getRange('Whole').insertText(text, Word.InsertLocation.replace);
      await context.sync();

      const afterInsertOoxml = newPara.getOoxml();
      await context.sync();
      const rawAfter = afterInsertOoxml?.value;
      if (rawAfter?.trim()) {
        let paraXml =
          DocumentReader.singleParagraphXmlFromGetOoxmlResult(afterInsertOoxml, text.trim()) || '';
        if (paraXml) {
          paraXml = TrackChangesParser.rewriteTrackChangeAuthorsInParagraphOoxml(paraXml, tcAuthor);
          if (
            await replaceParagraphViaLivePackage(rawAfter, paraXml, 'insertText-rewrite-author')
          ) {
            return newPara;
          }
        }
      }
    } finally {
      if (!trackingWasOn) await setTracking(false);
    }

    // Path B: handcrafted w:ins with explicit author in live package.
    const handcraftedXml = TrackChangesParser.createTrackedInsertionParagraphOoxml(
      text,
      tcAuthor,
      sourceParagraphOoxml,
    );
    const liveOoxml = newPara.getOoxml();
    await context.sync();
    const rawLive = liveOoxml?.value;
    if (rawLive?.trim()) {
      if (await replaceParagraphViaLivePackage(rawLive, handcraftedXml, 'handcrafted-w-ins')) {
        return newPara;
      }
    }

    const minimalXml = TrackChangesParser.createTrackedInsertionParagraphOoxml(
      text,
      tcAuthor,
      null,
    );
    if (rawLive?.trim()) {
      if (await replaceParagraphViaLivePackage(rawLive, minimalXml, 'handcrafted-minimal-pPr')) {
        return newPara;
      }
    }

    try {
      newPara.delete();
      await context.sync();
    } catch {
      /* best-effort cleanup */
    }
    throw new Error('Could not insert tracked paragraph with custom author');
  } finally {
    try {
      (doc as any).changeTrackingMode = previousMode;
      await context.sync();
    } catch {
      /* best-effort restore */
    }
  }
}

/** How many track-changes items share one context.sync() round trip in the fast
 * batched path. Larger = fewer round trips = faster, but a failure anywhere in a
 * chunk's sync is reported for the whole chunk (Office.js does not give reliable
 * per-call attribution inside one batch), so this bounds that blast radius. */
const TRACKED_BATCH_CHUNK_SIZE = 15;

type TrackedPlaceholder = {
  para: Word.Paragraph;
  text: string;
  tcAuthor: string;
  seq: number;
  ooxml?: { value?: string };
};

/**
 * Batched counterpart to insertTrackedParagraphAfterAnchor: instead of ~5-6
 * context.sync() round trips PER item (insert placeholder, toggle tracking,
 * insertText, getOoxml, rewrite-and-replace, restore tracking — "clause by
 * clause"), this does each PHASE once across up to TRACKED_BATCH_CHUNK_SIZE
 * items at a time. Same OOXML trick (Path A only — let Word generate w:ins via
 * native insertText, then rewrite just the w:author and round-trip via
 * insertOoxml): 100 items that took ~5-6 sync round trips *each* now take
 * roughly 5-6 round trips total per chunk of 15.
 *
 * Placeholders already exist (created — and style-assigned — in the same
 * unsynced pass as the plain-text items, by the caller) so this only handles
 * the tracked-text phases. A chunk-sync failure degrades rather than drops data:
 * - insertText chunk fails → those paragraphs stay empty; reported as failed.
 * - getOoxml / rewrite-replace chunk fails → text is already correctly
 *   inserted and natively tracked from the insertText phase (tracking was on),
 *   just possibly under the wrong author — counted as succeeded, logged as a
 *   degraded author attribution rather than retried (Path B/C's handcrafted-XML
 *   fallback is not worth re-deriving per item in the fast path; failures here
 *   should be rare since Path A is Word's own generated markup).
 */
async function insertTrackedParagraphsBatch(
  context: Word.RequestContext,
  placeholders: TrackedPlaceholder[],
  previousTrackingMode: string,
  details: string[],
): Promise<{ succeededSeqs: Set<number>; authorDegradedSeqs: Set<number> }> {
  const succeededSeqs = new Set<number>();
  const authorDegradedSeqs = new Set<number>();
  if (!placeholders.length) return { succeededSeqs, authorDegradedSeqs };

  const trackingWasOn = isChangeTrackingOn(previousTrackingMode);
  if (!trackingWasOn) {
    await setChangeTrackingMode(context, 'TrackAll');
  }

  const chunks: TrackedPlaceholder[][] = [];
  for (let i = 0; i < placeholders.length; i += TRACKED_BATCH_CHUNK_SIZE) {
    chunks.push(placeholders.slice(i, i + TRACKED_BATCH_CHUNK_SIZE));
  }

  // Phase A: insertText for every placeholder (Word auto-generates w:ins under
  // tracking) — a chunk that fails here has no salvageable content, so those
  // items are reported failed and excluded from later phases.
  const readyForOoxml: TrackedPlaceholder[] = [];
  for (const chunk of chunks) {
    for (const item of chunk) {
      item.para.getRange('Whole').insertText(item.text, Word.InsertLocation.replace);
    }
    try {
      await context.sync();
      readyForOoxml.push(...chunk);
    } catch (e: unknown) {
      logError('[AgentDocumentInserter] batched insertText chunk failed', {
        chunkSize: chunk.length,
        office: formatOfficeJsError(e),
      });
      for (const item of chunk) {
        details.push(`Insertion ${item.seq}: batched insertText failed`);
      }
    }
  }

  // Phase B: read back the OOXML Word generated, chunked.
  const readyForReplace: TrackedPlaceholder[] = [];
  for (let i = 0; i < readyForOoxml.length; i += TRACKED_BATCH_CHUNK_SIZE) {
    const chunk = readyForOoxml.slice(i, i + TRACKED_BATCH_CHUNK_SIZE);
    for (const item of chunk) {
      item.ooxml = item.para.getOoxml();
    }
    try {
      await context.sync();
      readyForReplace.push(...chunk);
    } catch (e: unknown) {
      logError('[AgentDocumentInserter] batched getOoxml chunk failed', {
        chunkSize: chunk.length,
        office: formatOfficeJsError(e),
      });
      for (const item of chunk) {
        succeededSeqs.add(item.seq);
        authorDegradedSeqs.add(item.seq);
      }
    }
  }

  // Phase C: tracking must be off for the insertOoxml replace trick (it carries
  // its own explicit w:ins with the rewritten author).
  await setChangeTrackingMode(context, 'Off');

  // Phase D: rewrite w:author in each item's own OOXML and replace, chunked.
  for (let i = 0; i < readyForReplace.length; i += TRACKED_BATCH_CHUNK_SIZE) {
    const chunk = readyForReplace.slice(i, i + TRACKED_BATCH_CHUNK_SIZE);
    const queued: TrackedPlaceholder[] = [];
    for (const item of chunk) {
      const rawAfter = item.ooxml?.value;
      if (!rawAfter?.trim()) {
        succeededSeqs.add(item.seq);
        authorDegradedSeqs.add(item.seq);
        continue;
      }
      let paraXml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(item.ooxml, item.text.trim()) || '';
      if (!paraXml) {
        succeededSeqs.add(item.seq);
        authorDegradedSeqs.add(item.seq);
        continue;
      }
      paraXml = TrackChangesParser.rewriteTrackChangeAuthorsInParagraphOoxml(paraXml, item.tcAuthor);
      const pruned = OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(rawAfter, item.text.trim()) || rawAfter;
      const packageXml = OoxmlPackageWrapper.replaceParagraphInPackage(pruned, paraXml);
      if (!packageXml?.trim()) {
        succeededSeqs.add(item.seq);
        authorDegradedSeqs.add(item.seq);
        continue;
      }
      const range = item.para.getRange('Whole') as Word.Range & {
        insertOoxml?: (xml: string, loc: Word.InsertLocation) => void;
      };
      if (typeof range.insertOoxml !== 'function') {
        succeededSeqs.add(item.seq);
        authorDegradedSeqs.add(item.seq);
        continue;
      }
      range.insertOoxml(packageXml, Word.InsertLocation.replace);
      queued.push(item);
    }
    if (queued.length) {
      try {
        await context.sync();
        for (const item of queued) succeededSeqs.add(item.seq);
      } catch (e: unknown) {
        logError('[AgentDocumentInserter] batched insertOoxml replace chunk failed', {
          chunkSize: queued.length,
          office: formatOfficeJsError(e),
        });
        for (const item of queued) {
          succeededSeqs.add(item.seq);
          authorDegradedSeqs.add(item.seq);
        }
      }
    }
  }

  // Phase E: restore whatever tracking mode was active before this batch.
  await setChangeTrackingMode(context, previousTrackingMode);

  if (authorDegradedSeqs.size) {
    log('[AgentDocumentInserter] batched tracked insert: author attribution degraded for some items', {
      count: authorDegradedSeqs.size,
      seqs: [...authorDegradedSeqs],
    });
  }

  return { succeededSeqs, authorDegradedSeqs };
}

/**
 * Insert many paragraphs at one or more agent paragraph anchors in a single Word.run().
 * Uses live Word paragraph objects (not re-resolved indices) so earlier insertions cannot
 * invalidate later anchors within the same batch.
 */
export async function insertAtMultipleAgentAnchors(
  insertions: MultiAnchorInsertion[],
): Promise<{ ok: boolean; succeeded: number; failed: number; details: string[] }> {
  const valid = insertions.filter((i) => i.text.trim().length > 0);
  if (!valid.length) {
    return { ok: false, succeeded: 0, failed: 0, details: ['No insertions'] };
  }

  const details: string[] = [];
  let succeeded = 0;
  let failed = 0;

  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      const pc = body.paragraphs;
      pc.load('items');
      await context.sync();
      const items = pc.items as any[];
      for (const item of items) item.load('text');
      await context.sync();

      const wordText = (i: number) => String(items[i]?.text ?? '').trim();
      const agentToWord = buildAgentIndexToWordIdx(items, wordText);
      const maxAgent = agentToWord.size > 0 ? Math.max(...agentToWord.keys()) : -1;

      const groups = new Map<number, MultiAnchorInsertion[]>();
      for (const ins of valid) {
        const idx = Math.max(0, Math.floor(ins.afterAgentParagraphIndex));
        if (!groups.has(idx)) groups.set(idx, []);
        groups.get(idx)!.push(ins);
      }

      const allPlain = valid.every((ins) => !ins.trackChangesAuthor?.trim());
      const previousTrackingMode = await readChangeTrackingMode(context);
      const trackingWasOn = isChangeTrackingOn(previousTrackingMode);
      if (allPlain && trackingWasOn) {
        await setChangeTrackingMode(context, 'Off');
        log('[AgentDocumentInserter] disabled track changes for plain-text batch');
      }

      const styleScanIndices = new Set<number>();
      for (const agentIdx of groups.keys()) {
        const wi = agentToWord.get(agentIdx);
        if (wi === undefined) continue;
        for (let j = wi; j < Math.min(wi + 25, items.length); j++) {
          styleScanIndices.add(j);
        }
      }
      const stylesByWordIdx = await loadParagraphStylesForIndices(
        context,
        items,
        [...styleScanIndices],
      );

      const sortedTargets = [...groups.keys()].sort((a, b) => b - a);
      let seq = 0;
      // Every item in this skill's usage shares one format_mode (the lawyer picks
      // plain or track-changes once per run — see execute_email_client_request_merge),
      // so a real batch is either allPlain (already fast — see below) or allTracked.
      // A literally mixed batch is not something this skill produces; keep the
      // original slow-but-simple per-item path for it rather than complicating the
      // fast path for a case that does not occur here.
      const allTracked = valid.every((i) => !!i.trackChangesAuthor?.trim());

      try {
        if (allTracked) {
          // Fast path: create all placeholders + assign style in one unsynced pass,
          // then run the whole tracked-insert dance in a handful of chunked syncs
          // total instead of ~5-6 PER item — see insertTrackedParagraphsBatch.
          const placeholders: TrackedPlaceholder[] = [];
          for (const agentIdx of sortedTargets) {
            const batch = groups.get(agentIdx)!;
            const wordIdx = agentToWord.get(agentIdx);
            if (wordIdx === undefined) {
              for (const _ of batch) {
                seq++;
                failed++;
                details.push(
                  `Insertion ${seq}: agent paragraph index ${agentIdx} not found (max ${maxAgent})`,
                );
              }
              continue;
            }
            let anchor: any = items[wordIdx];
            const styleRef = resolveInsertionStyleReference(wordIdx, items.length, wordText, stylesByWordIdx);
            for (const ins of batch) {
              seq++;
              anchor = anchor.insertParagraph('', Word.InsertLocation.after);
              applyInsertionParagraphStyleFast(anchor, styleRef, stylesByWordIdx);
              placeholders.push({ para: anchor, text: ins.text.trim(), tcAuthor: ins.trackChangesAuthor!.trim(), seq });
            }
          }

          await syncWithLabel(context, 'fast-path placeholders + style');

          const { succeededSeqs, authorDegradedSeqs } = await insertTrackedParagraphsBatch(
            context,
            placeholders,
            previousTrackingMode,
            details,
          );
          for (const item of placeholders) {
            if (succeededSeqs.has(item.seq)) {
              succeeded++;
              details.push(
                `Insertion ${item.seq}: inserted (author=${item.tcAuthor}${authorDegradedSeqs.has(item.seq) ? ', attribution degraded — see logs' : ''})`,
              );
            } else {
              failed++;
              // insertTrackedParagraphsBatch already pushed the specific failure detail.
            }
          }
        } else {
          for (const agentIdx of sortedTargets) {
            const batch = groups.get(agentIdx)!;
            const wordIdx = agentToWord.get(agentIdx);
            if (wordIdx === undefined) {
              for (const _ of batch) {
                seq++;
                failed++;
                details.push(
                  `Insertion ${seq}: agent paragraph index ${agentIdx} not found (max ${maxAgent})`,
                );
              }
              continue;
            }

            let anchor: any = items[wordIdx];
            const styleRef = resolveInsertionStyleReference(
              wordIdx,
              items.length,
              wordText,
              stylesByWordIdx,
            );
            const styleSourceParagraph =
              styleRef.refWordIdx != null ? (items[styleRef.refWordIdx] as Word.Paragraph) : undefined;

            for (const ins of batch) {
              const text = ins.text.trim();
              const tcAuthor = ins.trackChangesAuthor?.trim();
              seq++;
              try {
                if (tcAuthor) {
                  anchor = await insertTrackedParagraphAfterAnchor(
                    context,
                    anchor,
                    text,
                    tcAuthor,
                    styleSourceParagraph,
                  );
                  await applyInsertionParagraphStyle(context, anchor, styleRef, items);
                } else {
                  const needsPerInsertToggle = !allPlain && trackingWasOn;
                  if (needsPerInsertToggle) {
                    await setChangeTrackingMode(context, 'Off');
                  }
                  try {
                    anchor = anchor.insertParagraph(text, Word.InsertLocation.after);
                  } finally {
                    if (needsPerInsertToggle) {
                      await setChangeTrackingMode(context, previousTrackingMode);
                    }
                  }
                  await applyInsertionParagraphStyle(context, anchor, styleRef, items);
                }
                succeeded++;
                details.push(
                  `Insertion ${seq}: inserted after paragraph ${agentIdx}${tcAuthor ? ` (author=${tcAuthor})` : ''}`,
                );
              } catch (e: unknown) {
                failed++;
                const officeErr = formatOfficeJsError(e);
                logError('[AgentDocumentInserter] insertion failed', {
                  seq,
                  agentIdx,
                  tcAuthor: tcAuthor ?? null,
                  office: officeErr,
                });
                const msg =
                  e instanceof Error && e.message
                    ? e.message
                    : typeof officeErr.message === 'string'
                      ? officeErr.message
                      : 'insert failed';
                details.push(`Insertion ${seq}: ${msg}`);
              }
            }
          }

          await context.sync();
        }

        const needsRtl = valid.some((i) => textLooksHebrew(i.text));
        if (needsRtl) {
          let sectionRtlOk = await trySetSectionRtl(context);
          if (!sectionRtlOk) {
            const n = await prependRlmToHebrewParagraphs(context);
            log('[AgentDocumentInserter] batch RTL RLM fallback', { paragraphs: n });
          }
        }
      } finally {
        if (allPlain && trackingWasOn) {
          await setChangeTrackingMode(context, previousTrackingMode);
          log('[AgentDocumentInserter] restored track changes after plain-text batch');
        }
      }
    });

    log('[AgentDocumentInserter] batch inserted', { succeeded, failed, anchors: valid.length });
    return { ok: succeeded > 0, succeeded, failed, details };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[AgentDocumentInserter] batch failed', { msg, office: formatOfficeJsError(e) });
    return {
      ok: false,
      succeeded,
      failed: valid.length - succeeded,
      details: [...details, msg],
    };
  }
}

export type SkillReposition = {
  /** Exact text of a paragraph this same session inserted moments ago — the
   * post-execute self-review reposition flow only ever moves a paragraph whose
   * text matches something we ourselves just wrote, so exact-match is reliable
   * (not a fuzzy "find the right paragraph" guess). */
  matchText: string;
  targetAgentParagraphIndex: number;
  trackChangesAuthor?: string;
};

/** Word (or our own RTL fallback, prependRlmToHebrewParagraphs) prepends an RLM
 * to Hebrew paragraphs on insert — live paragraph.text is not byte-identical to
 * the client_request_text we sent, so a raw === comparison always misses. Strip
 * bidi control marks before comparing (same set AgentStructureApplier/DocumentReader
 * already strip when matching paragraph text elsewhere in this codebase).
 *
 * Also strip a leading Word-native numbered-list marker: inserting a NEW item
 * anywhere earlier in the same list auto-renumbers every later member, so a
 * paragraph captured as "16. …" can legitimately read "108. …" moments later
 * with byte-identical content otherwise — a raw match would then miss the
 * paragraph precisely because other insertions in this same run changed its
 * number (mirrors _normalize_for_anchor_match on the engine side). */
function stripBidiMarks(text: string): string {
  return text
    .replace(/[‎‏؜]/g, '')
    .trim()
    .replace(/^\d+(?:\.\d+)*[.)]?\s+/, '')
    .trim();
}

/**
 * Match `para`'s list membership/level to `referencePara`'s — mirrors
 * DocumentRestructurer.ts's `attachToSameListAs` but, instead of throwing when
 * the reference isn't a list item, detaches `para` from whatever it inherited.
 * A brand-new heading needs BOTH branches: in a document like this one, real
 * headings are plain non-list paragraphs while the surrounding clauses are one
 * continuous numbered list, so a heading inserted next to a clause silently
 * inherits that list (see the caller's docstring for why); in a document where
 * headings themselves ARE numbered top-level list items with their own
 * sub-clauses one level deeper, the new heading must instead join THAT list at
 * THAT level, not be detached from it. Which branch applies is read from the
 * live reference paragraph, never assumed.
 */
async function matchParagraphListStructure(
  context: Word.RequestContext,
  label: string,
  para: Word.Paragraph,
  referencePara: Word.Paragraph,
): Promise<void> {
  // `.listItem` (not the `OrNullObject` variant) throws ItemNotFound when the
  // paragraph is not a list item — exactly the case for a genuine heading in
  // a document like this one. Reading through `listItemOrNullObject` first
  // and branching on `.isNullObject` is required to inspect a reference
  // paragraph without knowing in advance whether it's a list item at all.
  const refListItem = referencePara.listItemOrNullObject;
  refListItem.load(['isNullObject', 'level']);
  const paraListItem = para.listItemOrNullObject;
  paraListItem.load('isNullObject');
  await syncWithLabel(context, `${label} load reference+target list state`);

  if (refListItem.isNullObject) {
    if (!paraListItem.isNullObject) {
      para.detachFromList();
      await syncWithLabel(context, `${label} detachFromList (reference heading is not a list item)`);
    }
    return;
  }

  const list = referencePara.listOrNullObject;
  list.load('id');
  await syncWithLabel(context, `${label} load reference list id`);
  const listId = list.id;
  if (listId == null) return;
  const level = refListItem.level ?? 0;

  if (!paraListItem.isNullObject) {
    para.detachFromList();
    await syncWithLabel(context, `${label} detach before re-attach`);
  }
  para.attachToList(listId, level);
  await syncWithLabel(context, `${label} attachToList level=${level}`);
}

/**
 * Restyle the paragraph(s) matching each of `headingTexts` to structurally
 * match `referenceHeadingText` — an EXISTING heading in the same document —
 * used right after a batch insert to make a brand-new section heading (the
 * wizard's free-text "other" option, engine-tagged `paragraph_role: "heading"`)
 * actually look and behave like the document's other headings, not like a
 * regular clause with bold slapped on.
 *
 * Why a reference paragraph and not a fixed rule: a heading's own paragraph
 * `style` and list membership/level are exactly the "heading level" the
 * lawyer sees, and that structure varies by document — this one's headings
 * are plain unnumbered paragraphs, but another document's headings could
 * themselves be numbered top-level list items with lettered sub-clauses one
 * level below. Hardcoding "headings are never numbered" would silently
 * mis-style the new heading in that second case. Copying the real reference
 * paragraph's `style` and reading its actual list membership/level (via
 * `matchParagraphListStructure`) generalizes to both.
 *
 * Root cause this fixes: the generic per-anchor style copy used during the
 * main batch insert (`resolveInsertionStyleReference`) picks up whatever
 * paragraph happens to be adjacent in THIS insertion run, which — once an
 * earlier group's own clause item lands right before the heading item in the
 * same anchor's chain — is a numbered CLAUSE, not the true heading; the new
 * heading then silently inherits that clause's list membership and gets its
 * own auto-generated list number, structurally indistinguishable from a
 * regular clause. This pass re-resolves style/list structure from a genuine
 * heading paragraph instead, independent of insertion order.
 *
 * A separate follow-up pass rather than fixing it in-line during insertion:
 * the tracked-changes path round-trips new paragraphs through an OOXML
 * rewrite/replace (see insertTrackedParagraphsBatch), which can leave the
 * original Word.Paragraph object stale, so matching by text afterwards is the
 * only reliably live way to reach the actual inserted paragraph. Best effort —
 * a miss here still leaves the heading's text correctly placed, just
 * unstyled, so failures are logged and swallowed rather than surfaced.
 */
export async function applyHeadingStructureToInsertedText(
  headingTexts: string[],
  referenceHeadingText: string,
): Promise<void> {
  const targets = new Set(
    headingTexts.map((t) => stripBidiMarks(t)).filter((t) => t.length > 0),
  );
  const refNormalized = stripBidiMarks(referenceHeadingText || '');
  if (!targets.size || !refNormalized) return;
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      const pc = body.paragraphs;
      pc.load('items');
      await context.sync();
      const items = pc.items as any[] as Word.Paragraph[];
      for (const item of items) item.load('text');
      await context.sync();

      let referencePara: Word.Paragraph | null = null;
      const targetParas: Word.Paragraph[] = [];
      for (const item of items) {
        const normalized = stripBidiMarks(String(item.text ?? ''));
        if (!referencePara && normalized === refNormalized) referencePara = item;
        if (targets.has(normalized)) targetParas.push(item);
      }
      if (!referencePara || !targetParas.length) {
        log('[AgentDocumentInserter] applyHeadingStructureToInsertedText: reference or targets not found', {
          referenceFound: !!referencePara,
          targetsFound: targetParas.length,
          requested: targets.size,
        });
        return;
      }

      referencePara.load('style');
      await context.sync();
      const refStyle = String(referencePara.style ?? '').trim();

      for (const p of targetParas) {
        if (refStyle) p.style = refStyle;
        p.font.bold = true;
      }
      await context.sync();

      for (let i = 0; i < targetParas.length; i++) {
        await matchParagraphListStructure(context, `heading-structure[${i}]`, targetParas[i], referencePara);
      }

      log('[AgentDocumentInserter] matched new heading(s) to reference structure', {
        requested: targets.size,
        matched: targetParas.length,
        referenceStyle: refStyle,
      });
    });
  } catch (e: unknown) {
    logError('[AgentDocumentInserter] failed to match new heading structure to reference', {
      office: formatOfficeJsError(e),
    });
  }
}

/**
 * Move a just-inserted client request to a different anchor: insert a copy at the
 * new location FIRST (while the old paragraph's object reference is still live),
 * then delete the old paragraph. Doing it in that order means the new anchor —
 * resolved once, before any mutation — never gets invalidated by the delete.
 */
export async function moveSkillInsertions(
  moves: SkillReposition[],
): Promise<{ succeeded: number; failed: number; details: string[] }> {
  if (!moves.length) {
    return { succeeded: 0, failed: 0, details: ['No moves'] };
  }

  const details: string[] = [];
  let succeeded = 0;
  let failed = 0;

  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      const pc = body.paragraphs;
      pc.load('items');
      await context.sync();
      const items = pc.items as any[];
      for (const item of items) item.load('text');
      await context.sync();

      const wordText = (i: number) => String(items[i]?.text ?? '').trim();
      const agentToWord = buildAgentIndexToWordIdx(items, wordText);

      let seq = 0;
      for (const move of moves) {
        seq++;
        try {
          const matchText = move.matchText.trim();
          if (!matchText) throw new Error('empty matchText');
          const matchTextFolded = stripBidiMarks(matchText);

          let oldWordIdx = -1;
          for (let wi = 0; wi < items.length; wi++) {
            if (stripBidiMarks(wordText(wi)) === matchTextFolded) {
              oldWordIdx = wi;
              break;
            }
          }
          if (oldWordIdx < 0) {
            throw new Error('previously-inserted paragraph not found (text no longer matches live document)');
          }

          const targetAgentIdx = Math.max(0, Math.floor(move.targetAgentParagraphIndex));
          const targetWordIdx = agentToWord.get(targetAgentIdx);
          if (targetWordIdx === undefined) {
            throw new Error(`target paragraph index ${targetAgentIdx} not found`);
          }

          const oldPara = items[oldWordIdx] as Word.Paragraph;
          const anchorPara = items[targetWordIdx] as Word.Paragraph;
          const tcAuthor = move.trackChangesAuthor?.trim();

          if (tcAuthor) {
            await insertTrackedParagraphAfterAnchor(context, anchorPara, matchText, tcAuthor);
          } else {
            anchorPara.insertParagraph(matchText, Word.InsertLocation.after);
          }
          await syncWithLabel(context, `move ${seq} insert at new anchor`);

          oldPara.delete();
          await syncWithLabel(context, `move ${seq} delete old paragraph`);

          succeeded++;
          details.push(`Move ${seq}: moved to paragraph ${targetAgentIdx}`);
        } catch (e: unknown) {
          failed++;
          const officeErr = formatOfficeJsError(e);
          logError('[AgentDocumentInserter] move failed', { seq, office: officeErr });
          const msg = e instanceof Error && e.message ? e.message : 'move failed';
          details.push(`Move ${seq}: ${msg}`);
        }
      }

      const needsRtl = moves.some((m) => textLooksHebrew(m.matchText));
      if (needsRtl) {
        const sectionRtlOk = await trySetSectionRtl(context);
        if (!sectionRtlOk) {
          const n = await prependRlmToHebrewParagraphs(context);
          log('[AgentDocumentInserter] move RTL RLM fallback', { paragraphs: n });
        }
      }
    });

    log('[AgentDocumentInserter] moves applied', { succeeded, failed, moves: moves.length });
    return { succeeded, failed, details };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[AgentDocumentInserter] moves batch failed', { msg, office: formatOfficeJsError(e) });
    return { succeeded, failed: moves.length - succeeded, details: [...details, msg] };
  }
}
